import React, { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  PiClockCounterClockwiseLight,
  PiCurrencyDollarLight,
  PiKeyLight,
  PiShieldCheckLight,
  PiUserLight,
} from 'react-icons/pi';
import { useAppDispatch, RootState } from '../../store';
import apis from '../../utils/apis';
import logger from '../../utils/logger';
import { isMatterEconomicsEnabled } from '../../utils/config';
import DetailFrame from '../shared/Frames/DetailFrame';
import Button from '../shared/TailwindComponents/Button';
import Tabs from '../shared/TailwindComponents/Tabs';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import EmptyState from '../shared/TailwindComponents/EmptyState';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { showModal } from '../../store/actions/modalActions';
import AdminDetailHeader from './AdminDetailHeader';
import { ADMIN_USERS_SECTION_PATH } from './adminRoutes';
import usePeopleDirectory from './people/usePeopleDirectory';
import { isUuidLike } from './people/peopleDirectory';
import { useOptionalBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import UserIdentityCard from './userEdit/UserIdentityCard';
import UserAccessPanel from './userEdit/UserAccessPanel';
import UserLicensePanel from './userEdit/UserLicensePanel';
import UserPermissionsPanel from './userEdit/UserPermissionsPanel';
import UserRatesPanel from './userEdit/UserRatesPanel';
import UserActivityPanel from './userEdit/UserActivityPanel';
import {
  AdminUser,
  PermissionSection,
  UserPermission,
  findAdminUser,
  visibleUserPermissions,
} from './userEdit/userEditModel';
import { readErrorMessage } from '../../utils/errorMessage';

/**
 * One person, administered.
 *
 * This page used to be 1,750 lines: the licence panel, the permission grid,
 * the identity header and the plan picker all lived in the same file, so a
 * change to how a plan row looks meant scrolling past the permission toggle
 * handler to find it. It now only fetches, holds the writes that more than one
 * panel needs, and chooses which pane is on screen — the panes themselves are
 * in `./userEdit/`, one file per thing an admin might come here to do.
 */

const USERS_PATH = ADMIN_USERS_SECTION_PATH;

type PageTab = 'overview' | 'licensing' | 'permissions' | 'rates' | 'activity';

export interface AdminUserEditProps {
  /**
   * The record to show — a uuid, or a numeric id from a link written before
   * uuids were in the URL. Falls back to the route's `:userId`/`:detailId`.
   */
  userId?: string;
  /**
   * Rendered inside the settings shell: no `DetailFrame`, because the shell
   * already draws the page chrome and the rail.
   */
  embedded?: boolean;
}

/** What the load produced. `missing` is a real answer, not a stuck spinner. */
type LoadState = 'loading' | 'ready' | 'missing' | 'error';

const AdminUserEdit: React.FC<AdminUserEditProps> = ({ userId: userIdProp, embedded = false }) => {
  const { t } = useTranslation();
  const params = useParams<{ userId?: string; detailId?: string }>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const { directory, ready: directoryReady } = usePeopleDirectory();
  const avatarDirectory = directory.avatars;
  const breadcrumbsContext = useOptionalBreadcrumbs();

  // The id can arrive three ways: as a prop (the section renders the sub-view),
  // as `:detailId` (/settings/admin-users/<uuid>) or as `:userId` (the old
  // route). Whichever it is, it may be a uuid or a legacy numeric id.
  const userId = userIdProp || params.detailId || params.userId || '';

  const [roster, setRoster] = useState<AdminUser[] | null>(null);
  const [rosterFailed, setRosterFailed] = useState(false);
  const [permissionSections, setPermissionSections] = useState<PermissionSection[]>([]);
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [pageTab, setPageTab] = useState<PageTab>('overview');
  const [licenseSummary, setLicenseSummary] = useState<any>(null);

  const orgType =
    (userInfo as any)?.organization_type || (userInfo as any)?.OrganizationType || 'law_firm';

  /**
   * Who may write here. The permissions endpoint 403s anyone who is not an org
   * admin — including a person editing their own record — so the tab is
   * read-only for them rather than offering a Save that cannot succeed.
   */
  const isOrgAdmin = !!(userInfo as any)?.is_org_admin;

  const fetchUserPermissions = useCallback(async (id: number) => {
    try {
      const response = await apis.get(`/admin/users/${id}/permissions`);
      return (response.data?.success ? response.data.data.permissions : []) || [];
    } catch (error: any) {
      logger.error('Error fetching user permissions', { status: error?.response?.status });
      return [];
    }
  }, []);

  /**
   * The roster this page finds its person in.
   *
   * There is no `/admin/users/:id` — the list is the only source — so the load
   * is ONE fetch of the organization, kept in state, and who the URL names is
   * then a pure question about it (`findAdminUser`). Separating the two is what
   * removes the stuck spinner: every outcome of the fetch (people, empty, or a
   * failure) ends the wait, and re-resolving after the uuid directory arrives
   * costs nothing rather than a second round trip.
   */
  const loadRoster = useCallback(
    async (announceFailure: boolean): Promise<AdminUser[] | null> => {
      setRosterFailed(false);
      try {
        const response = await apis.get('/admin/users');
        const people = (response.data?.data || []) as AdminUser[];
        setRoster(people);
        setRosterFailed(false);
        return people;
      } catch (error: any) {
        logger.error('Error fetching user', { status: error?.response?.status });
        setRosterFailed(true);
        if (announceFailure) {
          dispatch(showFeedbackModal({
            showModal: true,
            message: readErrorMessage(error, t('Failed to fetch user')),
            modalType: 'error',
            duration: 4000,
          }));
        }
        return null;
      }
    },
    // `t` is stable for a mounted component; keeping it out avoids a reload on
    // every language-file change while a person is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  useEffect(() => {
    // The roster is the same payload whichever person the URL names, so a
    // second load racing a first cannot produce a wrong answer — only the same
    // one twice.
    if (userId) loadRoster(true);
  }, [userId, loadRoster]);

  /** Who the URL names — a uuid through the directory, a legacy numeric id directly. */
  const user = useMemo<AdminUser | null>(() => {
    const found = findAdminUser(roster, userId, directory.userIdByUuid);
    if (!found) return null;
    // The roster has no uuid of its own; carrying the directory's one on the
    // record is what lets the panes below (activity) ask for it.
    const uuid = found.uuid || directory.uuidByUserId.get(found.id);
    return uuid && uuid !== found.uuid ? { ...found, uuid } : found;
  }, [roster, userId, directory]);

  const adminCount = useMemo(
    () => (roster || []).filter((person) => person.is_org_admin).length,
    [roster]
  );

  /**
   * What this person has, once we know who they are.
   *
   * Neither call gates the page: permissions fill the grid in when they land,
   * and a licence read that fails leaves the overview saying "no license data"
   * rather than holding the whole record hostage — which is what it did when
   * both lived inside the load.
   */
  useEffect(() => {
    const id = user?.id;
    if (id == null) {
      setUserPermissions([]);
      setLicenseSummary(null);
      return undefined;
    }

    let alive = true;
    fetchUserPermissions(id).then((permissions) => {
      if (alive) setUserPermissions(permissions);
    });
    apis
      .get(`/admin/users/${id}/licenses`)
      .then((response) => {
        if (alive && response.data?.success) setLicenseSummary(response.data.data);
      })
      .catch(() => {
        if (alive) setLicenseSummary(null);
      });

    return () => {
      alive = false;
    };
  }, [user?.id, fetchUserPermissions]);

  /**
   * What the page is doing, derived rather than remembered — a state that is
   * only ever computed cannot be left behind on `loading` by a path that
   * forgot to clear it.
   *
   * The one wait that is not the roster's: a uuid cannot be resolved until the
   * directory that translates it has arrived, so an unmatched uuid is still
   * loading while that request is in flight. A numeric id never waits for it.
   */
  const loadState: LoadState = !userId
    ? 'missing'
    : user
      ? 'ready'
      : rosterFailed
        ? 'error'
        : !roster || (isUuidLike(userId) && !directoryReady)
          ? 'loading'
          : 'missing';

  const loading = loadState === 'loading';

  /** Re-read the roster after an edit. The page keeps showing the person while
   *  it does — flipping back to a skeleton would read as a navigation rather
   *  than a save. */
  const refreshUser = useCallback(async () => {
    await loadRoster(false);
  }, [loadRoster]);

  const handleOpenEditModal = () => {
    if (!user) return;

    dispatch(showModal({
      showModal: true,
      type: 'adminEditUser',
      user,
      item: { adminCount },
      message: t('Edit User'),
      onConfirm: async (updatedUser: any) => {
        try {
          if (
            (updatedUser.role && updatedUser.role !== user.role) ||
            updatedUser.is_org_admin !== user.is_org_admin
          ) {
            await apis.post(`/admin/users/${user.id}/roles`, {
              role_name: updatedUser.role,
              is_org_admin: updatedUser.is_org_admin,
            });
          }

          dispatch(showFeedbackModal({
            showModal: true,
            message: t('User updated successfully'),
            modalType: 'success',
            duration: 3000,
          }));
          await refreshUser();
        } catch (error: any) {
          logger.error('Error updating user', { status: error?.response?.status });
          dispatch(showFeedbackModal({
            showModal: true,
            message: readErrorMessage(error, t('Failed to update user')),
            modalType: 'error',
            duration: 4000,
          }));
        }
      },
    }));
  };

  /**
   * Write this person's permissions — the COMPLETE desired direct set, in one
   * request.
   *
   * It used to be a POST or a DELETE per toggle, which meant a five-permission
   * change was five requests that could half-fail and leave the grid showing a
   * state the server never held. The endpoint takes the whole set and answers
   * with `data.current`: the full EFFECTIVE set, each entry tagged with where
   * it came from. That answer is what re-renders the grid — never the ids we
   * just sent, which know nothing about what the role adds.
   */
  const savePermissions = async (permissionIds: number[]) => {
    if (!user) return;

    try {
      const response = await apis.put(`/admin/users/${user.id}/permissions`, {
        permission_ids: permissionIds,
      });
      const current = response.data?.data?.current;
      if (Array.isArray(current)) setUserPermissions(current as UserPermission[]);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Permissions updated successfully'),
        modalType: 'success',
        duration: 2000,
      }));
    } catch (error: any) {
      const status = error?.response?.status;
      logger.error('Error saving permissions', { status });
      dispatch(showFeedbackModal({
        showModal: true,
        // 403 is the backend refusing a non-admin (including a self-edit); 400
        // is a rule it enforces, and its own wording — "cannot remove the last
        // admin" — says more than any message we could invent here.
        message:
          status === 403
            ? t('Only an organization admin can change permissions.')
            : readErrorMessage(error, t('Failed to update permissions')),
        modalType: 'error',
        duration: 4000,
      }));
      throw error;
    }
  };

  // The full permission catalogue is only needed by the permissions pane —
  // fetch it when that pane is first opened rather than on every page load.
  useEffect(() => {
    if (pageTab !== 'permissions' || permissionSections.length > 0) return;
    let alive = true;
    apis
      .get('/admin/permissions')
      .then((response) => {
        if (alive && response.data.success) setPermissionSections(response.data.data || []);
      })
      .catch((error: any) => {
        logger.error('Error fetching permissions', { status: error?.response?.status });
      });
    return () => { alive = false; };
  }, [pageTab, permissionSections.length]);

  const shownPermissions = useMemo(
    () => visibleUserPermissions(userPermissions),
    [userPermissions]
  );

  const breadcrumbs = (current: string) => [
    { name: t('Settings'), href: '/settings', current: false },
    { name: t('Users'), href: USERS_PATH, current: false },
    { name: current, href: '#', current: true },
  ];

  /** The person, as everything that names them says it. */
  const personName = user ? `${user.first_name} ${user.last_name}`.trim() || user.email : '';

  /**
   * What the trail's last segment says — the same word in the shell's
   * breadcrumb and in the detail's own, in every state.
   *
   * Never the id. A raw `48` in the breadcrumb tells the reader nothing they
   * cannot already see in the URL, and reads as a bug next to "Settings ›
   * Users"; while the record is in flight the honest answer is that it is
   * loading, and the skeleton under it says the same thing.
   */
  const crumbLabel = user
    ? personName
    : loading
      ? t('Loading...')
      : loadState === 'error'
        ? t('Error')
        : t('Not found');

  /**
   * The shell's breadcrumb, which otherwise derives itself from the URL and so
   * would show the uuid. Only when embedded: standalone, `DetailFrame` passes
   * its own trail to the header and a second source would fight it.
   */
  const setBreadcrumbs = breadcrumbsContext?.setBreadcrumbs;
  useEffect(() => {
    if (!embedded || !setBreadcrumbs) return undefined;
    setBreadcrumbs(breadcrumbs(crumbLabel));
    return () => setBreadcrumbs([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, setBreadcrumbs, crumbLabel, t]);

  /**
   * One frame, two hosts.
   *
   * Standalone the page brings its own `DetailFrame`; inside the settings
   * shell it must not, or the section grows a second header bar and a second
   * breadcrumb trail — which is exactly what the old top-level route looked
   * like next to the rest of settings.
   */
  const frame = (
    crumb: string,
    body: React.ReactNode,
    actions?: { label: string; onClick: () => void; variant: 'primary' }[]
  ) =>
    embedded ? (
      <div className="flex h-full min-h-0 flex-col gap-4">
        <AdminDetailHeader
          backTo={USERS_PATH}
          sectionLabel={t('Users')}
          recordLabel={crumb}
          actions={actions?.map((action) => (
            <Button key={action.label} variant="primary" size="small" onClick={action.onClick}>
              {action.label}
            </Button>
          ))}
        />
        {body}
      </div>
    ) : (
      <DetailFrame breadcrumbs={breadcrumbs(crumb)} showBackButton actions={actions}>
        {body}
      </DetailFrame>
    );

  if (loading) {
    return frame(
      crumbLabel,
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-1">
        <Skeleton variant="card" />
        <Skeleton variant="form" />
      </div>
    );
  }

  /**
   * A failed load explains itself and offers Retry first — the usual cause is
   * transient, and the alternative used to be a trip back to the list to click
   * the same row again. A record that is genuinely gone says so instead.
   */
  if (!user) {
    return frame(
      crumbLabel,
      <EmptyState
        icon={<PiUserLight />}
        headline={loadState === 'error' ? t('Could not load this person') : t('User not found')}
        subtext={
          loadState === 'error'
            ? t('Something went wrong reading your organization. Try again in a moment.')
            : !userId
              ? t('This link is missing a user id.')
              : t('They may have been removed from your organization.')
        }
        button={
          loadState === 'error'
            ? { label: t('Retry'), onClick: () => { loadRoster(false); } }
            : { label: t('Back to users'), onClick: () => history.push(USERS_PATH) }
        }
      />
    );
  }

  const showRates = isMatterEconomicsEnabled();

  const pageTabs: { key: PageTab; label: string; icon: React.ReactNode }[] = [
    { key: 'overview', label: t('Overview'), icon: <PiUserLight className="h-4 w-4" /> },
    { key: 'licensing', label: t('Licensing'), icon: <PiShieldCheckLight className="h-4 w-4" /> },
    { key: 'permissions', label: t('Permissions'), icon: <PiKeyLight className="h-4 w-4" /> },
    ...(showRates
      ? [{ key: 'rates' as PageTab, label: t('Rates'), icon: <PiCurrencyDollarLight className="h-4 w-4" /> }]
      : []),
    { key: 'activity', label: t('Activity'), icon: <PiClockCounterClockwiseLight className="h-4 w-4" /> },
  ];

  const planName =
    licenseSummary?.base_plan?.display_name || licenseSummary?.base_plan?.name || null;

  return frame(
    crumbLabel,
    /* The detail is a flex column whose body scrolls — the identity card and
       the tab bar stay put while a long permission grid moves under them. */
    <div className={clsx('flex min-h-0 flex-1 flex-col gap-4', !embedded && 'p-4')}>
        <div className="flex-shrink-0 space-y-3">
          <UserIdentityCard user={user} planName={planName} avatarDirectory={avatarDirectory} />
          <Tabs
            tabs={pageTabs.map((tab) => ({ label: tab.label, value: tab.key, icon: tab.icon }))}
            activeTab={pageTab}
            onTabClick={(val) => setPageTab(val as PageTab)}
            compact
            badgeCounts={
              shownPermissions.length > 0 ? { permissions: shownPermissions.length } : undefined
            }
          />
        </div>

        <div className="custom-scrollbar-main min-h-0 flex-1 overflow-y-auto">
          {pageTab === 'overview' && (
            <UserAccessPanel
              user={user}
              licenseSummary={licenseSummary}
              permissions={shownPermissions}
              onManageLicense={() => setPageTab('licensing')}
              onViewPermissions={() => setPageTab('permissions')}
            />
          )}

          {pageTab === 'licensing' && <UserLicensePanel userId={user.id} />}

          {pageTab === 'permissions' && (
            <UserPermissionsPanel
              sections={permissionSections}
              userPermissions={userPermissions}
              orgType={orgType}
              canEdit={isOrgAdmin}
              onSave={savePermissions}
            />
          )}

          {pageTab === 'rates' && showRates && <UserRatesPanel userId={user.id} />}

          {pageTab === 'activity' && (
            <UserActivityPanel userUuid={user.uuid} userId={user.id} />
          )}
        </div>
    </div>,
    [{ label: t('Edit'), onClick: handleOpenEditModal, variant: 'primary' as const }]
  );
};

export default AdminUserEdit;
